import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { useRouter } from 'next/router'
import React, { ChangeEvent, memo, useEffect, useState } from 'react'
import { useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'

export interface SearchFieldProps {
  fontSize?: number
  height?: number
  className?: string
}

/**
 * Primary UI component for user interaction
 */
export const SearchField: React.FC<SearchFieldProps> = memo(({ className = '', ...props }) => {
  const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const [value, SetValue] = useState(router.query.keyword || '')
  const [tabSelected, SetTabSelected] = useState(router.query?.tabs || router.query?.tab || '')

  useEffect(() => {
    if (router.query.keyword) SetValue(router.query.keyword)
    else SetValue('')

    if (router.query?.tabs || router.query?.tab) {
      SetTabSelected((router.query?.tabs ?? router.query?.tab) || '')
    } else {
      SetTabSelected('directory')
    }
  }, [router.query])

  const onTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const target: HTMLInputElement = e.target as HTMLInputElement
    SetValue(target.value)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (value !== '') {
        const searchKeyword = (value as string).trim()
        SetValue(searchKeyword)
        updateQuery(searchKeyword)
      } else {
        updateQuery('')
      }
    }
  }

  const updateQuery = (keyword = '') => {
    const subTopicFound = data?.topics?.some((item) =>
      item?.subTopics?.some((sub) => sub?.name.toLowerCase() === decodeURIComponent(keyword as string).toLowerCase()),
    )
    const topicFound = data?.topics?.some(
      (item) => item?.name.toLowerCase() === decodeURIComponent(keyword as string).toLowerCase(),
    )

    // Conditionally add sort property
    const shouldIncludeSort =
      tabSelected === 'directory' ||
      tabSelected === 'graveyard' ||
      tabSelected === 'consolidations' ||
      tabSelected === ''
    const sortParam = keyword && shouldIncludeSort ? { sort: 'bykeyword' } : keyword === '' ? {} : {}

    if (keyword != '' && data && router.asPath.includes('/search') && (topicFound || subTopicFound)) {
      if (
        data?.topics?.some(
          (item: any) => item?.name.toLowerCase() === decodeURIComponent(keyword as string).toLowerCase(),
        )
      ) {
        const productType: string[] = []
        data?.topics
          ?.filter((item) => item?.name.toLowerCase() === decodeURIComponent(keyword as string).toLowerCase())
          .map((item) => {
            item?.subTopics?.map((subTopic: any) => {
              productType.push(item.documentId! + '!!' + subTopic.documentId!)
            })
          })

        updateQueryParams({
          keyword: keyword,
          productType: productType.join(','),
          ...sortParam,
        })
      } else {
        data?.topics?.map((item) => {
          item?.subTopics
            ?.filter((sub: any) => sub?.name.toLowerCase() === decodeURIComponent(keyword as string).toLowerCase())
            .map((sub: any) => {
              updateQueryParams({
                keyword: keyword,
                productType: item?.documentId + '!!' + sub?.documentId,
                ...sortParam,
              })
            })
        })
      }
    } else {
      updateQueryParams({
        keyword: keyword ? keyword : undefined,
        page: undefined,
        ...sortParam,
      })
    }
  }

  return (
    <div
      className={cn(
        'flex',
        'flex-row items-center',
        'border-mist border-solid border rounded-[20px] min-h-[42px] bg-white pl-[14px]',
        className,
      )}
      {...props}
    >
      <IonIcon name="search" />
      <input
        type="text"
        className="focus:outline-none w-full font-primary font-regular text-textdark opacity-70 text-[14px] mr-[14px] ml-[14px] leading-[42px]"
        placeholder="Search within"
        onChange={onTextChanged}
        onKeyPress={handleKeyPress}
        value={value}
        tabIndex={0}
      />
    </div>
  )
})
